import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** Per-viewer display preferences (SET-04 saves them to the profile as well). */
export type Theme = 'dark' | 'light';
export type Density = 'compact' | 'comfortable';

export interface Prefs {
  theme: Theme;
  density: Density;
  sound: boolean;
}

interface PrefsCtx extends Prefs {
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setSound: (s: boolean) => void;
}

const KEY = 'bx.prefs';
const DEFAULTS: Prefs = { theme: 'dark', density: 'compact', sound: true };

function read(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    /* storage unavailable */
  }
  return DEFAULTS;
}

const Ctx = createContext<PrefsCtx>({ ...DEFAULTS, setTheme: () => {}, setDensity: () => {}, setSound: () => {} });

export function usePrefs(): PrefsCtx {
  return useContext(Ctx);
}

export function PrefsProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<Prefs>(read);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', prefs.theme);
    document.documentElement.setAttribute('data-density', prefs.density);
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable */
    }
  }, [prefs]);

  const setTheme = useCallback((theme: Theme) => setPrefs((p) => ({ ...p, theme })), []);
  const setDensity = useCallback((density: Density) => setPrefs((p) => ({ ...p, density })), []);
  const setSound = useCallback((sound: boolean) => setPrefs((p) => ({ ...p, sound })), []);
  const value = useMemo(() => ({ ...prefs, setTheme, setDensity, setSound }), [prefs, setTheme, setDensity, setSound]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
